import {
    Component,
    DestroyRef,
    effect,
    WritableSignal,
    ViewChild,
    ElementRef,
    AfterViewInit,
    OnDestroy,
    signal,
    NgZone,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SharedModule } from '../../shared.module';
import {
    NavigationCancel,
    NavigationEnd,
    NavigationError,
    Router,
} from '@angular/router';
import {
    MenuItem,
    PageSidebarService,
} from '../../services/page-sidebar.service';
import {
    firstLeaf,
    NavLocationService,
} from '../../services/nav-location.service';
import { ClientSettingsService } from '../../services/client-settings.service';
import { Client } from '../../models/client';

@Component({
    selector: 'page-sidebar-menu',
    standalone: true,
    imports: [SharedModule],
    templateUrl: './page-sidebar-menu.component.html',
    styleUrls: ['./page-sidebar-menu.component.scss'],
})
export class PageSidebarMenuComponent implements AfterViewInit, OnDestroy {
    @ViewChild('sidebarContent') sidebarContent!: ElementRef<HTMLElement>;
    @ViewChild('menuList') menuList!: ElementRef<HTMLElement>;
    @ViewChild('promoBox') promoBox!: ElementRef<HTMLElement>;

    private resizeObserver: ResizeObserver | null = null;

    // Signal to control promo box visibility
    showPromoBox = signal(true);
    menuNeedsScroll = signal(false);
    client: WritableSignal<Client | undefined> =
        this.clientService.getClientSignal();

    isInMainNav = true;
    isInSubNav = false;

    menuItems: MenuItem[] = [];

    /** Resolved from the URL on every navigation -- never carried over. */
    currentParent?: MenuItem;
    currentItem?: MenuItem;

    // Collapse state from PageSidebarService
    isCollapsed = this.pageSidebarService.collapsed;
    sidebarWidth = this.pageSidebarService.sidenavWidth;

    private destroyRef = inject(DestroyRef);

    constructor(
        private router: Router,
        public pageSidebarService: PageSidebarService,
        private clientService: ClientSettingsService,
        private ngZone: NgZone,
        private navLocation: NavLocationService,
    ) {
        // only need to do this on construction (or refresh); not on every route change
        // Need to get dynamic menu items based on client type when client signal has loaded
        effect(() => {
            // React to client signal changes
            const client = this.client();

            // Also react to clientColumns signal changes (for client routes)
            const clientColumns = this.pageSidebarService.clientColumns();

            // Setup sidebar when either changes
            this.setupSidebar();

            // do this on construction, and every route change
            this.findMenuPosition();
        });

        // Load initial values before client has loaded
        this.setupSidebar();

        /** This has to live in constructor for it to work */
        this.router.events
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event: any) => {
                // NavigationCancel/NavigationError matter as much as the
                // success case: `canDeactivate: [unsavedChangesGuard]` is wired
                // to units/:id, units/:id/edit and the workflow editors, and
                // answering "Stay on page" cancels the navigation. Listening
                // only for NavigationEnd left the sidebar pointing at the
                // section the user clicked but never reached (RF-2776).
                if (
                    event instanceof NavigationEnd ||
                    event instanceof NavigationCancel ||
                    event instanceof NavigationError
                ) {
                    this.findMenuPosition();
                }
            });
    }

    setupSidebar() {
        if (this.router.url.includes('/tenant/'))
            this.menuItems = this.pageSidebarService.setupTenantColumns();
        else if (this.router.url.includes('/owner/'))
            this.menuItems = this.pageSidebarService.setupOwnerColumns();
        else if (this.router.url.includes('/admin/'))
            this.menuItems = this.pageSidebarService.setupAdminColumns();
        else this.menuItems = this.pageSidebarService.clientColumns();
    }

    /**
     * Resolve the sidebar's position from the current URL.
     *
     * Delegates to NavLocationService, which longest-prefix matches the URL
     * against the menu config. Everything is recomputed from the URL on each
     * call: the previous result is never reused, which is what made the old
     * implementation report a stale section after navigating to a route it
     * could not match (RF-2776).
     */
    findMenuPosition() {
        // `currentParent` is the section the user is demonstrably in: either
        // the one they just clicked (`doRouting()` sets it before navigating)
        // or, when they clicked a child row, the section whose sub nav they
        // were already in. It is passed only to break a tie between two menu
        // entries sharing one route -- see NavLocationService.bestMatch().
        // Without it, clicking Income would land in Bookkeeping, because the
        // landlord/PM menu lists Invoices and Payments under both (RF-2776).
        const location = this.navLocation.resolve(
            this.router.url,
            this.menuItems,
            this.currentParent,
        );

        this.currentParent = location.section ?? undefined;
        this.currentItem = location.item ?? undefined;
        this.isInSubNav = location.isInSubNav;
        this.isInMainNav = !location.isInSubNav;

        // Re-check promo visibility after menu position changes
        setTimeout(() => this.setupResizeObserver(), 50);
    }

    goBackToMainNav() {
        this.isInMainNav = true;
        this.isInSubNav = false;
        // Re-check promo visibility after view updates
        setTimeout(() => this.setupResizeObserver(), 50);
    }

    toggleSidebar() {
        this.pageSidebarService.toggle();
    }

    /**
     * Supers only (RF-2798): switch to the redesigned nav. Only rendered on
     * the client portal, where the new nav exists.
     */
    showNewNavToggle(): boolean {
        return (
            this.pageSidebarService.isSuperUser() &&
            !this.router.url.includes('/tenant/') &&
            !this.router.url.includes('/owner/') &&
            !this.router.url.includes('/admin/')
        );
    }

    switchToNewNav() {
        this.pageSidebarService.setNewNavEnabled(true);
    }

    goToSubNav(parent: MenuItem) {
        // The first *leaf*, not children[0]: a route-less group as the first
        // child would otherwise navigate to '' -- the dashboard.
        const leaf = firstLeaf(parent);
        if (leaf) {
            this.router.navigate([leaf.route], {
                queryParams: leaf.queryParams,
            });
        } else {
            // use case is the portfolio page which uses switchers, not routes
            this.router.navigate([parent.route]);
        }

        this.isInMainNav = false;
        this.isInSubNav = true;
        // Re-check promo visibility after view updates
        setTimeout(() => this.setupResizeObserver(), 50);
    }

    doRouting(parent: MenuItem) {
        this.currentParent = parent;

        if (parent.children?.length) {
            this.goToSubNav(parent);
        } else {
            this.router.navigate([parent.route]);
        }
    }

    ngAfterViewInit() {
        // Set up ResizeObserver to detect when sidebar size changes
        this.resizeObserver = new ResizeObserver(() => {
            this.ngZone.run(() => this.checkPromoVisibility());
        });

        // Initial setup
        this.setupResizeObserver();
    }

    /**
     * Set up or refresh the ResizeObserver on current elements
     */
    private setupResizeObserver() {
        if (!this.resizeObserver) return;

        // Disconnect from any previous elements
        this.resizeObserver.disconnect();

        // Observe the sidebar content for size changes
        if (this.sidebarContent?.nativeElement) {
            this.resizeObserver.observe(this.sidebarContent.nativeElement);
        }

        // Check after a brief delay to let the DOM settle
        setTimeout(() => this.checkPromoVisibility(), 50);
    }

    ngOnDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    /**
     * Check if promo box should be visible.
     * Rules:
     * - If menu items alone don't fit (need scroll), hide promo
     * - If menu items + promo would cause scroll, hide promo
     * - Only show promo when everything fits without scrolling
     */
    private checkPromoVisibility() {
        if (
            !this.sidebarContent?.nativeElement ||
            !this.menuList?.nativeElement
        ) {
            // Default to showing promo when elements aren't ready
            this.showPromoBox.set(true);
            this.menuNeedsScroll.set(false);
            return;
        }

        const container = this.sidebarContent.nativeElement;
        const menuList = this.menuList.nativeElement;

        // Available height in the sidebar content area
        const availableHeight = container.clientHeight;

        // Height needed by menu items alone
        const menuHeight = menuList.scrollHeight;

        // Approximate promo box height (including margin/padding)
        // Breakdown: ~76px referral card content + 24px padding-top from .promo-content-box
        // Note: Using approximation instead of measuring promoBox.scrollHeight because
        // the promo box may not be rendered yet when visibility is being calculated
        const promoHeight = 100;

        // If we can't measure properly, default to showing promo
        if (availableHeight <= 0 || menuHeight <= 0) {
            this.showPromoBox.set(true);
            this.menuNeedsScroll.set(false);
            return;
        }

        // Check if menu items alone exceed available space
        const menuFits = menuHeight <= availableHeight;

        // Check if menu + promo would fit
        const menuAndPromoFit = menuHeight + promoHeight <= availableHeight;

        // Show promo only if everything fits without scrolling
        this.showPromoBox.set(menuAndPromoFit);

        // Enable scroll only if menu items alone don't fit
        this.menuNeedsScroll.set(!menuFits);
    }
}
